// src/components/Hermes/InputArea.tsx

import { useState, useRef } from 'react';
import type { KeyboardEvent, DragEvent } from 'react';
import { Send, Mic, Paperclip, Square } from 'lucide-react';
import type { InputAreaProps } from './types';
import type { RuntimeName } from '../../types/runtime';
import RuntimeHint from './RuntimeHint';
import VoiceVisualizer from './VoiceVisualizer';

export default function InputArea({
  onSend,
  onStop,
  loading = false,
  streaming = false,
  disabled = false,
  placeholder = 'Command Hermes...',
  runtimeHint = null,
}: InputAreaProps) {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // 语音错误提示（麦克风权限被拒 / 浏览器不支持等）
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 记录录音开始前的输入文本，便于转文字后拼接保留
  const preTextRef = useRef('');
  // 错误提示自动清除计时器
  const errorTimerRef = useRef<number | null>(null);

  // ============================================
  // 发送
  // ============================================

  const handleSend = () => {
    if (input.trim() && !loading && !disabled) {
      onSend(input.trim());
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  // ============================================
  // 停止
  // ============================================

  const handleStop = () => {
    if (onStop) {
      onStop();
    }
  };

  // ============================================
  // 语音
  // ============================================

  const handleVoice = () => {
    if (isRecording) {
      // 停止录音（VoiceVisualizer 通过 isActive=false 自动停止并触发转文字收尾）
      setIsRecording(false);
    } else {
      // 记录录音前的已有文本，转文字后拼接到其后
      preTextRef.current = input;
      setVoiceError(null);
      setIsRecording(true);
    }
  };

  // 语音转文字回调：实时把识别文本回填到输入框（保留录音前的文本）
  const handleTranscript = (text: string) => {
    const pre = preTextRef.current.trim();
    const next = pre ? `${pre} ${text}` : text;
    setInput(next);
    // 同步调整 textarea 高度（setInput 不会触发 onChange 的自适应）
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
      }
    });
  };

  // 语音错误回调：显示提示并停止录音态，5 秒后自动清除
  const handleVoiceError = (err: string) => {
    setVoiceError(err);
    setIsRecording(false);
    if (errorTimerRef.current) window.clearTimeout(errorTimerRef.current);
    errorTimerRef.current = window.setTimeout(() => setVoiceError(null), 5000);
  };

  // ============================================
  // 文件上传
  // ============================================

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      console.log('[Hermes] Files selected:', files);
      // TODO: 处理文件上传
      // 通过 RuntimeManager 分发到对应的 Runtime
    }
    // 重置 input，允许重复选择同一文件
    e.target.value = '';
  };

  // ============================================
  // 拖拽
  // ============================================

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log('[Hermes] Files dropped:', files);
      // TODO: 处理文件拖拽上传
      // 通过 RuntimeManager 分发到对应的 Runtime
    }
  };

  // ============================================
  // 渲染
  // ============================================

  const isStreaming = streaming || loading;

  return (
    <div
      className={`
        border-t border-white/5 p-3 bg-white/5
        ${isDragging ? 'ring-2 ring-blue-500/50 ring-inset' : ''}
      `}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Runtime 提示 */}
      <RuntimeHint runtime={runtimeHint as RuntimeName | null | undefined} loading={loading} />

      {/* 语音音域可视化（录音时显示，高度 120px 撑满） */}
      <VoiceVisualizer
        isActive={isRecording}
        onTranscript={handleTranscript}
        onError={handleVoiceError}
      />

      {/* 语音错误提示（麦克风权限被拒 / 浏览器不支持等） */}
      {voiceError && (
        <div className="mb-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5">
          ⚠️ {voiceError}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* 语音按钮 */}
        <button
          onClick={handleVoice}
          disabled={loading || disabled}
          className={`
            p-2 rounded-lg transition flex-shrink-0
            ${isRecording
              ? 'bg-red-500/30 text-red-400 animate-pulse'
              : 'text-gray-500 hover:text-white hover:bg-white/10'
            }
            ${(loading || disabled) ? 'opacity-30 cursor-not-allowed' : ''}
          `}
          title={isRecording ? '停止录音' : '语音输入'}
        >
          <Mic className="w-5 h-5" />
        </button>

        {/* 文件按钮 */}
        <button
          onClick={handleFileClick}
          disabled={loading || disabled}
          className={`
            p-2 rounded-lg transition flex-shrink-0
            text-gray-500 hover:text-white hover:bg-white/10
            ${(loading || disabled) ? 'opacity-30 cursor-not-allowed' : ''}
          `}
          title="上传文件"
        >
          <Paperclip className="w-5 h-5" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          multiple
        />

        {/* 输入框 */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? '🎤 正在录音...' : placeholder}
            disabled={loading || disabled || isRecording}
            className={`
              w-full resize-none rounded-xl bg-white/5 border border-white/10 px-4 py-2.5
              text-sm text-white placeholder-gray-500
              focus:outline-none focus:border-blue-500/50
              transition
              ${isRecording ? 'border-red-500/50' : ''}
              ${(loading || disabled) ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
        </div>

        {/* Stop / Send */}
        {isStreaming ? (
          <button
            onClick={handleStop}
            className="p-2 rounded-lg transition flex-shrink-0 bg-red-500/30 text-red-400 hover:bg-red-500/50"
            title="停止生成"
          >
            <Square className="w-5 h-5" />
          </button>
        ) : (
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading || disabled}
            className={`
              p-2 rounded-lg transition flex-shrink-0
              ${input.trim() && !loading && !disabled
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-white/5 text-gray-500 cursor-not-allowed'
              }
            `}
            title="发送"
          >
            <Send className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* 拖拽提示 */}
      {isDragging && (
        <div className="absolute inset-0 flex items-center justify-center bg-blue-500/10 backdrop-blur-sm rounded-xl pointer-events-none">
          <div className="text-blue-400 text-sm font-medium">
            📎 释放文件以上传
          </div>
        </div>
      )}
    </div>
  );
}